import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Label,
} from '@repo/shadcn';
import { useDebounce } from '@uidotdev/usehooks';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PiPlusCircleDuotone } from 'react-icons/pi';

import { AutoCompleteInput } from '../../../../components/ui/autocomplete/auto-complete-input';
import { MODAL_ROUTE } from '../../../../constants';
import { useGetBrandsQuery } from '../../../../features/slice/products/brand/brand-api-slice';
import { useModal } from '../../../../hooks';
import { TProductForm } from './product-form.component';

export const BrandSelector: FC = () => {
    const [searchInputText, setSearchInputText] = useState('');
    const { openModal } = useModal();

    const { setValue, watch, control } = useFormContext<TProductForm>();

    const selectedBrandId = watch('brand_id');
    const selectedBrandName = watch('brand_name');

    const debouncedSearchInputText = useDebounce(searchInputText, 500);

    const { data, isLoading, isFetching } = useGetBrandsQuery({
        search: debouncedSearchInputText,
    });

    const handleInputChange = (value: string) => {
        setSearchInputText(value);
    };

    const handleValueChange = (value: any) => {
        if (!value) {
            setValue('brand_id', '');
            setValue('brand_name', '');
            return;
        }

        setValue('brand_id', value.value);
        setValue('brand_name', value.label);
    };

    const generateOptions = () => {
        const options =
            data?.data.map((brand: any) => ({
                label: brand.name,
                value: brand.id,
            })) || [];

        // add selected option if not in the list
        if (
            selectedBrandId &&
            selectedBrandName &&
            !options.find((opt: any) => opt.value === selectedBrandId)
        ) {
            options.unshift({
                label: selectedBrandName,
                value: selectedBrandId,
            });
        }

        return options;
    };

    const options = generateOptions();

    const selectedOption = {
        label: selectedBrandName || '',
        value: selectedBrandId || '',
    };

    return (
        <FormField
            control={control}
            name={'brand_id'}
            render={() => (
                <FormItem>
                    <FormLabel className="flex items-center justify-between">
                        <div>
                            <span>Brand</span>
                            <span className="ml-1 text-red-500">*</span>
                        </div>

                        <Label
                            onClick={() =>
                                openModal(MODAL_ROUTE.CREATE_PRODUCT_BRAND)
                            }
                            className="flex cursor-pointer items-center gap-1 text-primary hover:underline"
                        >
                            <PiPlusCircleDuotone size={16} className="-mt-1" />
                            Add Brand
                        </Label>
                    </FormLabel>

                    <FormControl>
                        <AutoCompleteInput
                            isLoading={isLoading || isFetching}
                            options={options}
                            showSuggestionsOnFocus={true}
                            value={selectedOption}
                            emptyMessage={'No items found'}
                            onValueChange={handleValueChange}
                            onInputChange={handleInputChange}
                        />
                    </FormControl>

                    <FormMessage className="line-clamp-1 text-xs" />
                </FormItem>
            )}
        />
    );
};

BrandSelector.displayName = 'BrandSelector';
